import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ErrorMsg from "../../ErrorMessage/ErrorMsg";
import LoadingComponent from "../../LoadingComp/LoadingComponent";
import { loginUserAction } from "../../../redux/slices/users/usersSlice";
import { resetErrAction } from "../../../redux/slices/globalActions/globalActions";

const Login = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  
  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });
  
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  
  const { email, password } = formData;
  
  // Get state from Redux
  const { userAuth } = useSelector((state) => state?.users || {});
  const reduxError = userAuth?.error;
  const userInfo = userAuth?.userInfo;
  
  // ADD THIS useEffect FOR ADMIN REDIRECT
  useEffect(() => {
    if (userInfo) {
      if (userInfo?.isAdmin) {
        navigate("/admin");
      } else {
        navigate("/");
      }
    }
  }, [userInfo, navigate]);
  
  // Handle showing error from Redux
  useEffect(() => {
    if (reduxError && reduxError.msg) {
      setErrorMessage(reduxError.msg);
      setShowError(true);
    }
  }, [reduxError]);
  
  const onChangeHandler = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (showError) {
      setShowError(false);
      setErrorMessage("");
      dispatch(resetErrAction());
    }
  };
  
  const onBlurHandler = (e) => {
    setTouched({ ...touched, [e.target.name]: true });
  };
  
  const onSubmitHandler = async (e) => {
    e.preventDefault();
    
    setShowError(false);
    setErrorMessage("");
    
    if (!email.trim() || !password.trim()) {
      setErrorMessage("Please fill in all fields");
      setShowError(true);
      return;
    }
    
    await dispatch(loginUserAction({ 
      email: email.trim(), 
      password 
    }));
  };
  
  useEffect(() => {
    return () => {
      dispatch(resetErrAction());
    };
  }, [dispatch]);
  
  const isEmailValid = touched.email && !email.trim();
  const isPasswordValid = touched.password && !password.trim();
  
  return (
    <>
      {showError && <ErrorMsg message={errorMessage} />}
      
      <section className="relative overflow-x-hidden">
        <div className="container px-4 mx-auto">
          <div className="flex flex-wrap items-center">
            <div className="w-full lg:w-2/6 px-4 mb-12 lg:mb-0">
              <div className="py-20 text-center">
                <h3 className="mb-8 text-4xl md:text-5xl font-bold font-heading">
                  {t("login.title")}
                </h3>
                <p className="mb-10 font-semibold font-heading">
                  {t("login.subtitle")}
                </p>
                
                <form onSubmit={onSubmitHandler}>
                  <div className="mb-4">
                    <input
                      name="email"
                      value={email}
                      onChange={onChangeHandler}
                      onBlur={onBlurHandler}
                      className={`ui-input ux-transition ux-focus-ring w-full px-12 py-6 ${
                        isEmailValid ? "border-ui-danger" : ""
                      }`}
                      type="email"
                      placeholder={t("login.email")}
                      autoComplete="email"
                    />
                    {isEmailValid && (
                      <p className="text-ui-danger text-xs mt-1 text-left">
                        {t("login.emailRequired")}
                      </p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <input
                      name="password"
                      value={password}
                      onChange={onChangeHandler}
                      onBlur={onBlurHandler}
                      className={`ui-input ux-transition ux-focus-ring w-full px-12 py-6 ${
                        isPasswordValid ? "border-ui-danger" : ""
                      }`}
                      type="password"
                      placeholder={t("login.password")}
                      autoComplete="current-password"
                    />
                    {isPasswordValid && (
                      <p className="text-ui-danger text-xs mt-1 text-left">
                        {t("login.passwordRequired")}
                      </p>
                    )}
                  </div>
                  
                  {userAuth?.loading ? (
                    <div className="flex justify-center mt-12 md:mt-16">
                      <LoadingComponent />
                    </div>
                  ) : (
                      <button
                        type="submit"
                        className="ui-btn-primary ux-transition ux-focus-ring hover:bg-ui-primary-hover mt-12 md:mt-16 font-bold font-heading py-5 px-8 uppercase w-full"
                      >
                        {t("login.loginButton")}
                      </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
        <div
          className="hidden lg:block lg:absolute top-0 bottom-0 right-0 lg:w-3/6 bg-center bg-cover bg-no-repeat"
          style={{
            backgroundImage:
              'url("https://res.cloudinary.com/dc6xthiol/image/upload/v1774647717/register_divg2g.png")',
          }}
        />
      </section>
    </>
  );
};

export default Login;